import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { CheckCircle, X } from "@phosphor-icons/react";

interface ToastMessage {
  id: number;
  title: string;
  message?: string;
}

const ToastContext = createContext<
  ((title: string, message?: string) => void) | null
>(null);

export function ToastProvider({ children }: PropsWithChildren) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const notify = useCallback((title: string, message?: string) => {
    const id = Date.now();
    setMessages((current) => [...current, { id, title, message }]);
    window.setTimeout(() => {
      setMessages((current) => current.filter((item) => item.id !== id));
    }, 4200);
  }, []);
  const value = useMemo(() => notify, [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {messages.map((toast) => (
          <div className="toast" key={toast.id}>
            <CheckCircle size={21} weight="fill" />
            <span>
              <strong>{toast.title}</strong>
              {toast.message && <small>{toast.message}</small>}
            </span>
            <button
              aria-label="关闭通知"
              onClick={() =>
                setMessages((current) =>
                  current.filter((item) => item.id !== toast.id),
                )
              }
            >
              <X size={15} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("ToastProvider is missing");
  return context;
}
